import type { Article } from "../../../../types/content";

import {
  getAdId,
  getApnTag,
  getGenericExternalId,
  getLayout,
  getSupplyType,
  sanitizeValue,
  windowKeyWords,
} from ".";
import { getClientCookie } from "../../../../utils/getClientCookie";
import { getPublisher } from "./getPublisher";

interface GetKeywordsProps {
  user: { isLoggedIn: boolean; isPremium: boolean };
  topicName?: string;
  geoLocationTracking?: boolean;
  article?: Article;
  consentToAll: boolean | null;
}

type Keywords = Record<string, string | number | string[] | undefined>;

// Cache SDK version globally to avoid repeated apntag fetches
// Note: SDK version is static per session, while other keywords (article, topic, etc.)
// are dynamic and recalculated per call. This cache only stores the version string.
let cachedSdkVersion: string | null = null;
let sdkVersionPromise: Promise<string> | null = null;

/**
 * Get the AST SDK version, cached after first fetch
 * This optimization prevents repeated apntag polling in getKeywords
 */
async function getAstSdkVersion(): Promise<string> {
  // Return cached version if available
  if (cachedSdkVersion !== null) {
    return cachedSdkVersion;
  }

  // Return pending promise if fetch is in progress
  if (sdkVersionPromise) {
    return sdkVersionPromise;
  }

  // Fetch and cache the SDK version
  sdkVersionPromise = getApnTag()
    .then((apntag) => {
      const version =
        apntag && typeof apntag.getAstVersion === "function"
          ? apntag.getAstVersion()
          : "";
      cachedSdkVersion = version;
      sdkVersionPromise = null;
      return version;
    })
    .catch(() => {
      // On error, cache empty string and don't retry
      cachedSdkVersion = "";
      sdkVersionPromise = null;
      return "";
    });

  return sdkVersionPromise;
}

const getKeywords: (
  getKeywordProps: GetKeywordsProps,
) => Promise<Keywords> = async ({
  user,
  topicName,
  geoLocationTracking,
  article,
  consentToAll,
}) => {
  const override =
    process.env.NEXT_PUBLIC_UI_THEME === "core"
      ? {}
      : {
          "se-omni-section": "Ekonomi",
          "se-generic-section": "Ekonomi",
        };

  // Fetch only adId (SDK version is cached separately)
  const [sdkVersion, adId] = await Promise.all([getAstSdkVersion(), getAdId()]);

  const articleSpecific =
    article && Object.keys(article).length > 0 && article.constructor === Object
      ? {
          "se-generic-page": "article",
          "se-generic-articleid": article.article_id,
          "se-generic-story": sanitizeValue(
            article.story ? article.story.title : undefined,
          ),
          "se-generic-tags": article.tags.map((tag) =>
            sanitizeValue(tag.title),
          ),
          "se-generic-topic": sanitizeValue(article.title.value),
          "se-omni-section": sanitizeValue(article.category.title),
          "se-generic-section": sanitizeValue(article.category.title),
          ...(article.ad_keywords || {}),
          ...override,
        }
      : {};

  const geoLocationSpecific = geoLocationTracking
    ? {
        "se-generic-audience": (getClientCookie("nPsegs") || "").split("|"),
      }
    : {};

  const topicSpecific = topicName
    ? {
        "se-generic-topic": sanitizeValue(topicName),
      }
    : {};

  return {
    "se-generic-sdk-version": sdkVersion,
    "se-generic-page": "index",
    "aa-sch-country_code": "se",
    "aa-sch-publisher": getPublisher(),
    "aa-sch-supply_type": `${getSupplyType()}`,
    "aa-sch-cmp_advertising": consentToAll ? "1" : "0",
    "se-generic-appmode": "notinapp",
    "se-generic-pagegen": "smp",
    "se-generic-loggedin": `${user?.isLoggedIn}`,
    "se-generic-premium": `${user?.isPremium}`,
    "se-generic-sitemode": getLayout(),
    "se-generic-screenwidth": window.outerWidth,
    "se-generic-screenheight": window.outerHeight,
    "se-generic-viewportwidth": window.innerWidth,
    "se-generic-viewportheight": window.innerHeight,
    "se-generic-externalid_src": getGenericExternalId({
      adId,
      loggedIn: user.isLoggedIn,
    }),
    ...geoLocationSpecific,
    ...articleSpecific,
    ...windowKeyWords(),
    ...topicSpecific,
  };
};

export { getKeywords };

export type { Keywords };
